// src/pages/Work/UseCases/UseCases.js
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import DevelopmentTools from './DevelopmentTools/DevelopmentTools';
import DesktopApp from './DesktopApp/DesktopApp';
import MobileApp from './MobileApp/MobileApp';
import './UseCases.css';

// We'll forward a ref so the sidebar can call a function on UseCases
function UseCases(props, ref) {
  const { onCaseSelect } = props;
  const [selectedCase, setSelectedCase] = useState(null);
  
  // Create refs for each use case component
  const devToolsRef = useRef(null);
  const desktopAppRef = useRef(null);
  const mobileAppRef = useRef(null);

  // Define the order of use cases for navigation
  const useCaseOrder = ['devtools', 'desktopapp', 'mobileapp'];

  // Function to get current index in the order
  const getCurrentIndex = () => useCaseOrder.indexOf(selectedCase);

  // Function to navigate to next/previous use case
  const navigateUseCase = (direction) => {
    const currentIndex = getCurrentIndex();
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % useCaseOrder.length;
    } else {
      newIndex = currentIndex === 0 ? useCaseOrder.length - 1 : currentIndex - 1;
    }
    
    handleSelectCase(useCaseOrder[newIndex]);
  };

  // Expose a function so the sidebar can set the selectedCase
  useImperativeHandle(ref, () => ({
    selectUseCase: (caseId) => {
      setSelectedCase(caseId);
      onCaseSelect(caseId);
    }
  }));

  // Function to handle case selection
  const handleSelectCase = (caseId) => {
    setSelectedCase(caseId);
    onCaseSelect(caseId);
    
    // Get the target ref based on the selected case
    let targetRef;
    switch(caseId) {
      case 'devtools':
        targetRef = devToolsRef;
        break;
      case 'desktopapp':
        targetRef = desktopAppRef;
        break;
      case 'mobileapp':
        targetRef = mobileAppRef;
        break;
      default:
        return;
    }

    // Scroll to the target element
    if (targetRef.current) {
      setTimeout(() => {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  };

  // Remove the useEffect since we handle scrolling in handleSelectCase
  useEffect(() => {
    // Only handle initial render or other effects if needed
  }, [selectedCase]);

  return (
    <div className="use-cases-container">
      <div className="main-heading-container">
        <h2 className="main-heading">Use Cases</h2>
        <span className="heading-subtitle">Case Study Structure</span>
      </div>

      {/* NDA section */}
      <div className="nda-section">
        <h2 className="nda-title">Under a Non-Disclosure Agreement</h2>
        <div className="nda-content">
          <p>
            Due to an <span className="highlight-blue">NDA</span>, <strong>I can't share</strong> specific <strong>details or showcase</strong> my work to protect the client's intellectual property.
          </p>
          <p>
            Over 5 years, I led design efforts, improved internal products, and solved key UX/UI challenges.
          </p>
        </div>
      </div>

      <div className="highlights-section">
        <p>Here are a few highlights of my <strong>contributions</strong> <span className="highlight-blue">3 Use Cases</span>:</p>
      </div>

      <div className="use-cases-grid">
        {/* Development Tools Box */}
        <div
          onClick={() => handleSelectCase('devtools')}
          className={`use-case-box ${selectedCase === 'devtools' ? 'active' : ''}`}
        >
          Development Tools
        </div>

        {/* Desktop App Box */}
        <div
          onClick={() => handleSelectCase('desktopapp')}
          className={`use-case-box ${selectedCase === 'desktopapp' ? 'active' : ''}`}
        >
          Desktop App
        </div>

        {/* Mobile App Box */}
        <div
          onClick={() => handleSelectCase('mobileapp')}
          className={`use-case-box ${selectedCase === 'mobileapp' ? 'active' : ''}`}
        >
          Mobile App
        </div>
      </div>

      {/* Always render all sections but control visibility with CSS */}
      <div style={{ 
        display: selectedCase === 'devtools' ? 'block' : 'none',
        width: '100%',
        marginBottom: '2rem'
      }}>
        <div ref={devToolsRef}>
          <DevelopmentTools 
            onNavigate={navigateUseCase} 
            isFirst={selectedCase === 'devtools'} 
            isLast={selectedCase === 'mobileapp'} 
          />
        </div>
      </div>
      <div style={{ 
        display: selectedCase === 'desktopapp' ? 'block' : 'none',
        width: '100%',
        marginBottom: '2rem'
      }}>
        <div ref={desktopAppRef}>
          <DesktopApp 
            onNavigate={navigateUseCase} 
            isFirst={selectedCase === 'devtools'} 
            isLast={selectedCase === 'mobileapp'} 
          />
        </div>
      </div>
      <div style={{ 
        display: selectedCase === 'mobileapp' ? 'block' : 'none',
        width: '100%',
        marginBottom: '2rem'
      }}>
        <div ref={mobileAppRef}>
          <MobileApp 
            onNavigate={navigateUseCase} 
            isFirst={selectedCase === 'devtools'} 
            isLast={selectedCase === 'mobileapp'} 
          />
        </div>
      </div>
    </div>
  );
}

// We use forwardRef so the Sidebar can call "selectUseCase" on this component
export default forwardRef(UseCases);